/**
 * Before or After — the rules.
 *
 * One event sits on the table. Another appears. Does it come before it or after
 * it in the Bible's story? Right, and it takes its place on the timeline and the
 * next one appears. Wrong, and the run is over.
 *
 * ── Why it gets harder ───────────────────────────────────────────────────────
 *
 * A random draw from eighty events is mostly easy — two events picked at random
 * are usually eras apart, and "was the crucifixion before or after the flood" is
 * not a question. The game is only interesting when the two are close, so the
 * gap narrows as the run goes on. That is the whole difficulty curve, and it is
 * why a long run is worth something.
 *
 * The first version closed that gap far too fast: by round eight it was asking
 * people to order events two apart out of seventy-six, which is a coin flip
 * dressed as a question, and most runs died around six. The curve now takes
 * about twice as long to tighten and never goes below two — so there is always
 * daylight between the cards, and being beaten always means you were beaten by
 * something knowable.
 */

import { TIMELINE } from '../data/timeline.js';

/**
 * How far apart, in the ordering, the two events may be at each round.
 *
 * Starts generous and closes slowly. The floor is two rather than one: two
 * adjacent events in the same episode — the bush and the plagues, the arrest
 * and the trial — are not a memory test, they are a guess, and losing a
 * fourteen-round run to a guess is the thing that makes people stop playing.
 */
const GAPS = [
  { min: 26, max: 62 },
  { min: 22, max: 54 },
  { min: 18, max: 46 },
  { min: 15, max: 40 },
  { min: 13, max: 34 },
  { min: 11, max: 30 },
  { min: 9, max: 26 },
  { min: 8, max: 23 },
  { min: 7, max: 20 },
  { min: 6, max: 18 },
  { min: 5, max: 16 },
  { min: 5, max: 14 },
  { min: 4, max: 12 },
  { min: 4, max: 11 },
  { min: 3, max: 10 },
  { min: 3, max: 9 },
  { min: 3, max: 8 },
  { min: 2, max: 7 },
  { min: 2, max: 6 },
  { min: 2, max: 5 },
];

export const gapFor = (round) => GAPS[Math.min(round, GAPS.length - 1)];

const pick = (list) => list[Math.floor(Math.random() * list.length)];

/** Anything is a fair opening card. */
export function firstEvent(exclude = new Set()) {
  const pool = TIMELINE.filter((e) => !exclude.has(e.id));
  return pick(pool.length ? pool : TIMELINE);
}

/**
 * The next card: close enough to be a question, not so close it is a coin
 * flip before the player has earned one.
 *
 * `seen` keeps a run from repeating an event, which would be both a free point
 * and a tell. If the window is empty — near the ends of the timeline, late in a
 * long run — it widens rather than giving up, because refusing to deal a card
 * would end a run the player had not lost.
 */
export function nextEvent(anchor, round, seen = new Set()) {
  const { min, max } = gapFor(round);

  for (let widen = 0; widen < 12; widen++) {
    const low = Math.max(2, min - widen);
    const high = max + widen * 4;
    const pool = TIMELINE.filter((event) => {
      if (seen.has(event.id) || event.id === anchor.id) return false;
      const distance = Math.abs(event.order - anchor.order);
      return distance >= low && distance <= high;
    });
    if (pool.length) return pick(pool);
  }

  const rest = TIMELINE.filter((e) => !seen.has(e.id) && e.id !== anchor.id);
  return rest.length ? pick(rest) : null;
}

/** True if `choice` ('before' | 'after') is right for `card` against `anchor`. */
export function isCorrect(anchor, card, choice) {
  const actual = card.order < anchor.order ? 'before' : 'after';
  return actual === choice;
}

/**
 * The share line, in the same shape as The Daily's.
 *
 * A number and a row of squares, no event names — posting a run must not hand
 * anyone the answers to the run they are about to play.
 */
export function shareRun(score, best) {
  const squares = '🟩'.repeat(Math.min(score, 10)) + (score > 10 ? `+${score - 10}` : '');
  return [
    'Scripture Journey — Before or After',
    `${squares || '⬛'}  ${score} in a row`,
    best > score ? `Best: ${best}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}
